import { useEffect, useRef, useState } from "react";
import { Trash2, FileUp, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import AuthImage from "../AuthImage";

/** 4-slot hero photo grid + unlimited Additional Photos gallery for a Deal.
 *  Photos are stored server-side via POST /api/deals/{id}/photos and
 *  rendered through a tokenized landlord GET endpoint. Until the deal is
 *  saved for the first time, photos can't be attached — we show a clear
 *  hint banner instead. */
export default function DealPhotosSection({ editing }) {
  const [photos, setPhotos] = useState(editing?.photos || []);
  const [busySlot, setBusySlot] = useState(null);
  // Bulk-upload progress state for AdditionalPhotosSection: { total, done, ok, fail }
  const [bulkProgress, setBulkProgress] = useState(null);

  useEffect(() => {
    // Re-fetch fresh photo list whenever the editing deal changes (the
    // parent only seeds editing.photos at openEdit; if the user uploads
    // here, we keep our own state).
    setPhotos(editing?.photos || []);
  }, [editing?.id]);

  if (!editing) {
    return (
      <div className="rounded-sm border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-xs text-slate-500" data-testid="deal-photos-hint">
        <span className="font-mono uppercase tracking-widest text-slate-400">Photos · </span>
        Save the deal first, then return here to upload property photos (drone shots, interior, renderings, comps). The first 4 render as the hero grid with captions; the rest go on a final Photo Gallery page in the PDF.
      </div>
    );
  }

  const upload = async (slotIdx, file) => {
    if (!file) return;
    setBusySlot(slotIdx);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/deals/${editing.id}/photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPhotos((prev) => [...prev, { ...data, _justUploaded: Date.now() }]);
      toast.success("Photo uploaded");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Upload failed");
    } finally {
      setBusySlot(null);
    }
  };

  // Bulk upload — picks one or more files at once via multi-select. Runs
  // up to BULK_CONCURRENCY uploads in parallel (worker-pool pattern) so a
  // sponsor dropping 20 phone photos gets snappy throughput without
  // hammering object storage with 20 simultaneous POSTs.
  const uploadMany = async (fileList) => {
    if (!fileList || !fileList.length) return;
    const files = Array.from(fileList);
    const BULK_CONCURRENCY = 3;
    setBusySlot("extra");
    setBulkProgress({ total: files.length, done: 0, ok: 0, fail: 0 });

    let cursor = 0;
    const stats = { ok: 0, fail: 0 };

    const worker = async () => {
      // each worker pulls the next index until the queue is empty
      while (true) {
        const idx = cursor;
        cursor += 1;
        if (idx >= files.length) return;
        const file = files[idx];
        try {
          const fd = new FormData();
          fd.append("file", file);
          const { data } = await api.post(`/deals/${editing.id}/photos`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          setPhotos((prev) => [...prev, { ...data, _justUploaded: Date.now() }]);
          stats.ok += 1;
        } catch (e) {
          stats.fail += 1;
          toast.error(formatApiError(e?.response?.data?.detail) || `Failed: ${file.name}`);
        } finally {
          setBulkProgress((p) =>
            p ? { ...p, done: p.done + 1, ok: stats.ok, fail: stats.fail } : p,
          );
        }
      }
    };

    try {
      const workers = Array.from(
        { length: Math.min(BULK_CONCURRENCY, files.length) },
        () => worker(),
      );
      await Promise.all(workers);
      if (stats.ok > 0) {
        toast.success(`${stats.ok} photo${stats.ok > 1 ? "s" : ""} uploaded`);
      }
    } finally {
      setBusySlot(null);
      // Leave the completed bar up for a moment so the sponsor sees the
      // final result, then clear.
      setTimeout(() => setBulkProgress(null), 1200);
    }
  };

  const remove = async (pid) => {
    if (!window.confirm("Remove this photo?")) return;
    try {
      await api.delete(`/deals/${editing.id}/photos/${pid}`);
      setPhotos((prev) => prev.filter((p) => p.id !== pid));
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Delete failed");
    }
  };

  const saveCaption = async (pid, caption) => {
    try {
      const { data } = await api.put(`/deals/${editing.id}/photos/${pid}`, { caption });
      setPhotos((prev) => prev.map((p) => (p.id === pid ? { ...p, caption: data.caption } : p)));
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Caption save failed");
    }
  };

  const suggestCaption = async (pid) => {
    // Returns array of suggestions; caller decides which to show.
    const { data } = await api.post(`/deals/${editing.id}/photos/${pid}/suggest-caption`);
    return Array.isArray(data?.suggestions) ? data.suggestions : [];
  };

  // First 4 = hero slots (captionable + render in 2x2 on page 1 of PDF).
  // Anything beyond index 3 = gallery photos (no caption, render on PDF final page).
  const slots = Array.from({ length: 4 }, (_, i) => photos[i] || null);
  const gallery = photos.slice(4);

  return (
    <div data-testid="deal-photos-section" className="space-y-5">
      <div>
        <div className="flex items-baseline justify-between mb-2">
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
            Hero Photos ({Math.min(photos.length, 4)} / 4)
          </div>
          <div className="text-[9px] text-slate-400">First 4 render on page 1 with captions</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {slots.map((photo, i) => (
            <PhotoSlot
              key={photo?.id || `slot-${i}`}
              slotIdx={i}
              photo={photo}
              dealId={editing.id}
              busy={busySlot === i}
              onUpload={(file) => upload(i, file)}
              onRemove={() => photo && remove(photo.id)}
              onCaptionSave={(cap) => photo && saveCaption(photo.id, cap)}
              onSuggestCaption={() => photo && suggestCaption(photo.id)}
            />
          ))}
        </div>
      </div>
      <AdditionalPhotosSection
        gallery={gallery}
        dealId={editing.id}
        busy={busySlot === "extra"}
        progress={bulkProgress}
        onUploadMany={uploadMany}
        onRemove={remove}
      />
    </div>
  );
}

/** Renders the "Additional Photos" gallery row — unlimited count, no caption
 *  input (PDF places these on a dedicated final gallery page). Supports
 *  multi-file selection on the hidden <input> AND drag-and-drop. */
function AdditionalPhotosSection({ gallery, dealId, busy, progress, onUploadMany, onRemove }) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const dragDepthRef = useRef(0);
  const pct = progress && progress.total > 0
    ? Math.round((progress.done / progress.total) * 100)
    : 0;
  const isComplete = progress && progress.done >= progress.total;

  // Filter dropped items to image files only (matches the <input accept=...>
  // list). Non-image drops are silently ignored so a stray PDF or zip never
  // hits the POST endpoint.
  const acceptedTypes = new Set([
    "image/jpeg", "image/png", "image/webp", "image/heic", "image/heif",
  ]);
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current = 0;
    setDragOver(false);
    const files = Array.from(e.dataTransfer?.files || []).filter(
      (f) => acceptedTypes.has((f.type || "").toLowerCase()) ||
             /\.(jpe?g|png|webp|heic|heif)$/i.test(f.name || ""),
    );
    if (files.length) onUploadMany(files);
  };
  const handleDragEnter = (e) => {
    e.preventDefault();
    if (!e.dataTransfer?.types?.includes("Files")) return;
    dragDepthRef.current += 1;
    setDragOver(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setDragOver(false);
  };
  const handleDragOver = (e) => {
    // Required to allow drop.
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
  };

  return (
    <div data-testid="deal-additional-photos-section">
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
          Additional Photos ({gallery.length})
        </div>
        <div className="text-[9px] text-slate-400">Drop files anywhere · or click Upload More · gallery prints on the PDF tear sheet final page</div>
      </div>
      {progress && (
        <div className="mb-2 rounded-sm border border-rose-200 bg-rose-50 px-2.5 py-2"
          data-testid="deal-additional-photos-progress">
          <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-widest mb-1">
            <span className="text-rose-700">
              {isComplete ? "Done" : "Uploading"} · {progress.done} / {progress.total}
            </span>
            <span className="text-rose-700">
              {progress.fail > 0 ? <span className="text-rose-800 font-bold">{progress.fail} failed</span> : null}
              {progress.fail > 0 ? " · " : ""}
              {pct}%
            </span>
          </div>
          <div className="h-1 w-full rounded-sm bg-rose-100 overflow-hidden">
            <div
              className="h-full bg-rose-400 transition-all duration-200"
              style={{ width: `${pct}%` }}
              data-testid="deal-additional-photos-progress-bar"
            />
          </div>
        </div>
      )}
      <div
        onDrop={handleDrop}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        data-testid="deal-additional-photos-dropzone"
        className={`relative grid grid-cols-3 md:grid-cols-6 gap-2 rounded-sm transition-colors ${
          dragOver
            ? "ring-2 ring-emerald-400 ring-offset-2 bg-emerald-50/40"
            : ""
        }`}
      >
        {dragOver && (
          <div
            className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-sm bg-emerald-50/70 z-10"
            data-testid="deal-additional-photos-dropzone-overlay"
          >
            <span className="text-xs font-mono uppercase tracking-widest text-emerald-700">
              Drop to upload
            </span>
          </div>
        )}
        {gallery.map((photo) => (
          <div key={photo.id} className="relative aspect-[4/3] rounded-sm overflow-hidden border border-slate-200 bg-slate-100 group"
            data-testid={`deal-gallery-photo-${photo.id}`}>
            <AuthImage
              src={`/api/deals/${dealId}/photos/${photo.id}`}
              cacheBust={photo._justUploaded}
              alt={photo.name || "Gallery photo"}
              className="w-full h-full object-cover"
              placeholder={<div className="w-full h-full animate-pulse bg-slate-100" />}
              errorFallback={
                <div className="w-full h-full flex items-center justify-center text-[10px] font-mono text-rose-500">
                  Failed
                </div>
              }
            />
            <button type="button"
              onClick={() => onRemove(photo.id)}
              data-testid={`deal-gallery-photo-remove-${photo.id}`}
              className="absolute top-1 right-1 p-0.5 rounded-sm bg-white/90 hover:bg-rose-50 text-rose-600 opacity-0 group-hover:opacity-100 transition-opacity"
              title="Remove">
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid="deal-additional-photos-upload-btn"
          className="aspect-[4/3] rounded-sm border-2 border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 hover:border-slate-400 flex flex-col items-center justify-center text-slate-500 hover:text-slate-700 transition-colors disabled:opacity-50"
        >
          {busy ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <FileUp className="w-4 h-4 mb-0.5" />
              <span className="text-[9px] font-mono uppercase tracking-widest text-center px-1">Upload more</span>
            </>
          )}
          <input
            ref={inputRef}
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
            className="hidden"
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              onUploadMany(files);
              if (inputRef.current) inputRef.current.value = "";
            }}
          />
        </button>
      </div>
    </div>
  );
}

function PhotoSlot({ slotIdx, photo, dealId, busy, onUpload, onRemove, onCaptionSave, onSuggestCaption }) {
  const inputRef = useRef(null);
  const [caption, setCaption] = useState(photo?.caption || "");
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    setCaption(photo?.caption || "");
    setSuggestions([]);
  }, [photo?.id, photo?.caption]);

  const commitCaption = () => {
    const next = (caption || "").trim();
    const prev = (photo?.caption || "").trim();
    if (next === prev) return;
    onCaptionSave?.(next);
  };

  const runSuggest = async () => {
    if (suggesting) return;
    setSuggesting(true);
    setSuggestions([]);
    try {
      const out = await onSuggestCaption?.();
      if (!out || !out.length) {
        toast.error("No suggestions returned");
        return;
      }
      setSuggestions(out);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "AI caption failed");
    } finally {
      setSuggesting(false);
    }
  };

  const pickSuggestion = (s) => {
    setCaption(s);
    setSuggestions([]);
    onCaptionSave?.(s);
  };

  if (photo) {
    return (
      <div className="space-y-1.5" data-testid={`deal-photo-slot-${slotIdx}`}>
        <div className="relative aspect-[4/3] rounded-sm overflow-hidden border border-slate-200 bg-slate-100 group">
          <AuthImage
            src={`/api/deals/${dealId}/photos/${photo.id}`}
            cacheBust={photo._justUploaded}
            alt={photo.name || `Photo ${slotIdx + 1}`}
            className="w-full h-full object-cover"
            placeholder={<div className="w-full h-full animate-pulse bg-slate-100" />}
            errorFallback={
              <div className="w-full h-full flex items-center justify-center text-[10px] font-mono text-rose-500">
                Failed to load
              </div>
            }
          />
          <button type="button" onClick={onRemove}
            data-testid={`deal-photo-remove-${slotIdx}`}
            className="absolute top-1.5 right-1.5 p-1 rounded-sm bg-white/90 hover:bg-rose-50 text-rose-600 opacity-0 group-hover:opacity-100 transition-opacity"
            title="Remove photo">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="relative flex items-stretch gap-1">
          <input
            type="text"
            value={caption}
            onChange={(e) => setCaption(e.target.value.slice(0, 200))}
            onBlur={commitCaption}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); }
            }}
            placeholder="Add caption (e.g. North-facing facade)"
            maxLength={200}
            data-testid={`deal-photo-caption-${slotIdx}`}
            className="flex-1 min-w-0 text-[11px] px-2 py-1 border border-slate-200 rounded-sm bg-white focus:outline-none focus:ring-1 focus:ring-slate-400 focus:border-slate-400 placeholder:text-slate-400"
          />
          <button
            type="button"
            onClick={runSuggest}
            disabled={suggesting}
            title="AI caption suggestions"
            data-testid={`deal-photo-caption-ai-${slotIdx}`}
            className="shrink-0 px-1.5 rounded-sm border border-violet-200 bg-violet-50 hover:bg-violet-100 text-violet-700 disabled:opacity-60 flex items-center justify-center"
          >
            {suggesting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
          </button>
          {suggestions.length > 0 && (
            <div
              className="absolute z-30 left-0 right-0 top-full mt-1 rounded-sm border border-violet-200 bg-white shadow-lg p-1.5 space-y-1"
              data-testid={`deal-photo-caption-suggestions-${slotIdx}`}
            >
              <div className="flex items-center justify-between px-1 pb-1 border-b border-violet-100">
                <span className="text-[9px] font-mono uppercase tracking-widest text-violet-600">AI Suggestions</span>
                <button type="button"
                  onClick={() => setSuggestions([])}
                  className="text-[10px] text-slate-400 hover:text-slate-600 leading-none"
                  data-testid={`deal-photo-caption-suggestions-close-${slotIdx}`}>×</button>
              </div>
              {suggestions.map((s, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => pickSuggestion(s)}
                  data-testid={`deal-photo-caption-suggestion-${slotIdx}-${idx}`}
                  className="w-full text-left text-[11px] px-2 py-1 rounded-sm hover:bg-violet-50 text-slate-700 leading-snug"
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        data-testid={`deal-photo-upload-${slotIdx}`}
        className="relative aspect-[4/3] w-full rounded-sm border-2 border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 hover:border-slate-400 flex flex-col items-center justify-center text-slate-500 hover:text-slate-700 transition-colors disabled:opacity-50"
      >
        {busy ? (
          <span className="text-xs font-mono uppercase tracking-widest">Uploading…</span>
        ) : (
          <>
            <FileUp className="w-5 h-5 mb-1" />
            <span className="text-[10px] font-mono uppercase tracking-widest">Add photo {slotIdx + 1}</span>
            <span className="text-[9px] text-slate-400 mt-0.5">JPG · PNG · max 10MB</span>
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
          className="hidden"
          onChange={(e) => onUpload(e.target.files?.[0])}
        />
      </button>
      <div className="h-[22px]" aria-hidden="true" />
    </div>
  );
}
